import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { DaoSpeakersService } from 'src/app/providers/db/dao-speakers.service';
import { IonInfiniteScroll, ModalController, NavController, Platform } from '@ionic/angular';
import { Events } from 'src/app/shared/services/global/events.service';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { GlobalService, AnalyticsService } from 'src/app/shared/services';
import { TypeVisionModule } from '../../models/type-vision-module';
import { DaoGroupsService } from 'src/app/providers/db/dao-groups.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TypeTracking } from 'src/app/enums/type-tracking';
import { PathComponent } from 'src/app/models/path/path-components';
import { TranslateService } from '@ngx-translate/core';
import { EventColors } from 'src/app/models/event-colors';
import { UserDataService } from 'src/app/shared/services/user-data/user-data.service';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DaoGalleryService } from 'src/app/providers/db/dao-gallery.service';

type ViewMode = 'list' | 'grid';

@Component({
    selector: 'app-speakers',
    templateUrl: './speakers.page.html',
    styleUrls: ['./speakers.page.scss']
})
export class SpeakersPage implements OnInit, OnDestroy {
    @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

    subscriptions: Subscription[] = [];
    searchText: string = '';
    searchSubject = new Subject<string>();
    lastSpeakers;
    speakers: Array<any> = [];
    loadingMoreSpeakers: boolean = false;
    searchedSpeakers: Array<any> = [];
    searchedSpeakersFixedArray: Array<any> = [];
    eventId: string = null;
    moduleId: string = null;
    loader: boolean = true;
    typeOrder: string;
    backBtn: boolean = true;
    allSpeakersList: Array<any> = [];
    eventColors: EventColors = new EventColors();
    menuBadge: number = 0;

    typeVisionModule = null;
    GLOBAL_VISION = TypeVisionModule.GLOBAL_VISION;
    DIVIDED_BY_GROUPS = TypeVisionModule.DIVIDED_BY_GROUPS;
    GROUP_VISION = TypeVisionModule.GROUP_VISION;
    GROUP_ACCESS_PERMISSION = TypeVisionModule.GROUP_ACCESS_PERMISSION;

    groups = [];
    userGroups: any = [];
    viewGroup: boolean = false;
    showNoResultImage = false;

    listCompany = [];
    listPost = [];
    filterBloc = true;
    isFilterOpen = true;
    allowFilterCompany = null;
    allowFilterPost = null;
    showLetters: boolean = true;
    emailVisibilty: boolean = false;
    checkedCompanies = [];
    checkedPosts = [];
    isMobile: boolean = false;
    sortOrder: string = 'asc';
    sortOptions: { value: string; label: string }[] = [];

    companyFilterText: string;
    postFilterText: string;
    language: string;

    viewMode: ViewMode;
    currentPage: number = 1;
    pageSize: number = 24;

    listGalleryPhoto: Array<any>;
    module: any = null;

    constructor(
        private dbSpeakers: DaoSpeakersService,
        private route: ActivatedRoute,
        private navCtrl: NavController,
        public global: GlobalService,
        private events: Events,
        private SAnalytics: AnalyticsService,
        private dbGroups: DaoGroupsService,
        private translateService: TranslateService,
        private modalCtrl: ModalController,
        private SUserData: UserDataService,
        private SEventData: EventDataService,
        private platform: Platform,
        private dbGallery: DaoGalleryService
    ) {
        const params = this.route.snapshot.params;
        this.eventId = params.eventId;
        this.moduleId = params.moduleId;

        this.language = this.SEventData.getLanguage();

        this.loadColors();

        this.SAnalytics.moduleAccess(this.eventId, this.moduleId);
        this.SAnalytics.createTrackRoadToModule(this.eventId, this.moduleId, TypeTracking.ACCESS_TO_SPEAKERS_MODULE);

        this.menuBadge = this.global.notificationBadge;

        this.events.subscribe('menuBadge', () => {
            this.menuBadge = this.global.notificationBadge;
        });

        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(value => {
            this.searchText = value;
            this.performSearch(value);
        });
    }

    get displaySpeakers(): any[] {
        if (this.searchText || this.hasActiveFilters) {
            return this.searchedSpeakers;
        }
        return this.speakers;
    }

    get hasActiveFilters(): boolean {
        return this.checkedCompanies.length > 0 || this.checkedPosts.length > 0;
    }

    get paginatedSpeakers(): any[] {
        const all = this.displaySpeakers;
        const start = (this.currentPage - 1) * this.pageSize;
        return all.slice(start, start + this.pageSize);
    }

    get totalPages(): number {
        return Math.ceil(this.displaySpeakers.length / this.pageSize) || 1;
    }

    get isLoadingMore(): boolean {
        return this.loadingMoreSpeakers;
    }

    loadColors(eventId: string = this.eventId) {
        const subscription = this.SEventData.getEventDataListener(eventId).subscribe((eventData) => {
            this.eventColors = eventData.colors;
        });
        this.subscriptions.push(subscription);
    }

    ngOnInit() {
        if (this.platform.is('ios') || this.platform.is('android')) {
            this.isMobile = true;
        }
        this.viewMode = this.isMobile ? 'list' : 'grid';
        if (this.global.previousPage == 'container') {
            this.backBtn = false;
        } else {
            this.backBtn = true;
        }
        this.startAll();
    }

    ngOnDestroy() {
        this.viewGroup = false;
        this.dbSpeakers.closeRefGetModule();
        this.dbGroups.closeRefContSpeakersOfGroup();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    trackByUid(index: number, item: any): string {
        return item?.uid || String(index);
    }

    toggleViewMode(mode: ViewMode) {
        this.viewMode = mode;
        this.currentPage = 1;
    }

    toggleFilter() {
        this.isFilterOpen = !this.isFilterOpen;
    }

    onSearchInput(ev: any) {
        const value = (ev?.target?.value || '').trim();
        this.searchSubject.next(value);
    }

    performSearch(value: string) {
        this.currentPage = 1;
        if (value.length >= 1) {
            const lowerValue = value.toLowerCase();
            const source = this.searchedSpeakersFixedArray.length > 0
                ? this.searchedSpeakersFixedArray
                : this.allSpeakersList;

            const filtered = source.filter(item => {
                const title = this.getSpeakerTitle(item.title);
                return (item.name && item.name.toLowerCase().includes(lowerValue)) ||
                    (title && title.toLowerCase().includes(lowerValue)) ||
                    (item.company && item.company.toLowerCase().includes(lowerValue));
            });
            this.searchedSpeakers = this.getSortedCopy(filtered);
            this.showNoResultImage = this.searchedSpeakers.length === 0;
        } else {
            this.searchedSpeakers = this.searchedSpeakersFixedArray.length > 0
                ? this.getSortedCopy(this.searchedSpeakersFixedArray)
                : [];
            this.showNoResultImage = false;
        }
    }

    resitFilter() {
        this.searchedSpeakers = this.searchedSpeakersFixedArray.length > 0
            ? this.getSortedCopy(this.searchedSpeakersFixedArray)
            : [];
        this.showNoResultImage = false;
        this.currentPage = 1;
    }

    clearFilters() {
        this.checkedCompanies = [];
        this.checkedPosts = [];
        this.searchedSpeakersFixedArray = [];
        this.searchedSpeakers = [];
        this.showNoResultImage = false;
        this.currentPage = 1;
    }

    onListScroll() {
        if (!this.viewport || !this.lastSpeakers || this.loadingMoreSpeakers) return;
        const end = this.viewport.measureScrollOffset('bottom');
        if (end < 200) {
            this.moreSpeakers(null);
        }
    }

    goToPage(direction: 'prev' | 'next') {
        if (this.loadingMoreSpeakers) return;
        if (direction === 'prev' && this.currentPage > 1) {
            this.currentPage--;
        } else if (direction === 'next' && this.currentPage < this.totalPages) {
            this.currentPage++;
            if (this.currentPage >= this.totalPages - 1 && this.lastSpeakers) {
                this.moreSpeakers(null);
            }
        }
    }

    setSortOrder(value: string) {
        if (this.sortOrder === value) return;
        this.sortOrder = value;
        this.currentPage = 1;

        if (this.allSpeakersList.length > 0) {
            this.allSpeakersList = this.getSortedCopy(this.allSpeakersList);
            this.speakers = [...this.allSpeakersList];
        } else {
            this.speakers = this.getSortedCopy(this.speakers);
        }
        this.lastSpeakers = null;

        this.searchedSpeakers = this.getSortedCopy(this.searchedSpeakers);
        this.searchedSpeakersFixedArray = this.getSortedCopy(this.searchedSpeakersFixedArray);
    }

    private getSortComparator(): (a: any, b: any) => number {
        const multiplier = this.sortOrder === 'desc' ? -1 : 1;
        return (a, b) => {
            const nameA = a.queryName || a.name || '';
            const nameB = b.queryName || b.name || '';
            if (nameA < nameB) return -1 * multiplier;
            if (nameA > nameB) return 1 * multiplier;
            return 0;
        };
    }

    private getSortedCopy(array: any[]): any[] {
        return [...array].sort(this.getSortComparator());
    }

    headerHeightFn(item, index) {
        return 35;
    }

    itemHeightFn(item, index) {
        return 100;
    }

    speakerHeader() {
        const self = this;
        return function (speaker, i, speakers) {
            if (!self.showLetters) return null;
            if (i == 0) return speaker.letter;
            if (i != 0 && speaker.letter != speakers[i - 1].letter) return speaker.letter;
        };
    }

    startAll() {
        this.dbSpeakers.getModule(this.moduleId, async (module) => {
            this.module = module;
            this.allowFilterCompany = module.allowFilterCompany !== undefined ? module.allowFilterCompany : false;
            this.allowFilterPost = module.allowFilterPost !== undefined ? module.allowFilterPost : false;
            this.filterBloc = (this.allowFilterCompany === true || this.allowFilterPost === true);
            this.isFilterOpen = this.isMobile ? false : this.filterBloc;
            this.showLetters = module.showLetters === true;
            this.emailVisibilty = module.emailVisibilty !== undefined ? module.emailVisibilty : false;
            this.companyFilterText = module.companyFilterText
                ? module.companyFilterText
                : this.translateService.instant('pages.attendees.company');
            this.postFilterText = module.postFilterText
                ? module.postFilterText
                : this.translateService.instant('pages.attendees.post');

            this.typeOrder = module.orderUsers || 'asc';
            this.typeVisionModule = module.typeVision;
            this.sortOrder = this.typeOrder === 'desc' ? 'desc' : 'asc';
            this.sortOptions = [
                { value: 'asc', label: this.translateService.instant('pages.attendees.sort_az') },
                { value: 'desc', label: this.translateService.instant('pages.attendees.sort_za') },
            ];

            if (this.typeVisionModule === this.GLOBAL_VISION) {
                this.loader = true;
                this.viewGroup = false;
                this.getFirstSpeakersGlobalVision();
                this.getSpeakersAllGlobalVision();
            }

            if (this.typeVisionModule === this.DIVIDED_BY_GROUPS) {
                this.loader = true;
                this.viewGroup = true;
                this.getGroupsEvent();
            }

            if (this.typeVisionModule === this.GROUP_VISION) {
                this.loader = true;
                this.viewGroup = false;
                const userData = await this.SUserData.getUserDataSnapshot(this.eventId);
                if (userData) { this.userGroups = userData.groups; }
                this.getSpeakersGroupVision();
            }

            if (this.typeVisionModule === this.GROUP_ACCESS_PERMISSION) {
                this.loader = true;
                this.viewGroup = false;
                this.getFirstSpeakersLimitedAccessByGroups();
                this.getSpeakersAllLimitedAccessByGroups();
            }

            this.getGalleryImages();
        });
    }

    getGroupsEvent() {
        this.dbGroups.getGroups(this.eventId, (groups) => {
            this.groups = groups;

            groups.sort((a, b) => {
                return a.queryName < b.queryName ? -1 : a.queryName > b.queryName ? 1 : 0;
            });

            for (const group of groups) {
                const groupId = group.uid;
                const moduleSpeakerId = this.moduleId;
                group.qtdSessions = 0;

                this.dbGroups.contSpeakersOfGroup(groupId, moduleSpeakerId, (qtd: number) => {
                    group.qtdSessions = qtd;
                });
            }

            this.loader = false;
        });
    }

    profileDetails(speaker) {
        const navigationExtras: NavigationExtras = {
            state: {
                user: speaker,
                emailVisibilty: this.emailVisibilty
            }
        };
        this.navCtrl.navigateForward(
            `/event/${this.eventId}/profile/${this.moduleId}/4/${speaker.uid}`,
            navigationExtras
        );
        this.global.previousPage = null;
    }

    moreSpeakers(event) {
        if (this.typeVisionModule === this.GLOBAL_VISION) {
            this.moreSpeakersGlobalVision(event);
        } else if (this.typeVisionModule === this.GROUP_ACCESS_PERMISSION) {
            this.moreSpeakersLimitedAccessByGroups(event);
        } else {
            if (event?.target) event.target.disabled = true;
        }
    }

    getFirstSpeakersGlobalVision() {
        this.subscriptions.push(this.dbSpeakers.getFirstASpeakersGlobalVision(this.typeOrder, this.moduleId).subscribe((data) => {
            if (data.length > 0) {
                this.lastSpeakers = data[data.length - 1];
            }

            const filteredList = data.map(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getSpeakerTitle(element.title);
                return element;
            });

            this.speakers = filteredList;
            this.buildFilterLists(filteredList);
            this.loader = false;
        }));
    }

    getSpeakersAllGlobalVision() {
        this.subscriptions.push(this.dbSpeakers.getSpeakersAllGlobalVision(this.moduleId).subscribe((speakers) => {
            this.allSpeakersList = speakers;
            this.buildFilterLists(speakers);
        }));
    }

    private deduplicateSpeakers(list: any[]): any[] {
        const seen = new Set<string>();
        return list.filter(item => {
            if (!item?.uid || seen.has(item.uid)) return false;
            seen.add(item.uid);
            return true;
        });
    }

    moreSpeakersGlobalVision(event) {
        if (this.loadingMoreSpeakers) return;
        this.loadingMoreSpeakers = true;

        this.dbSpeakers.getNextPageSpeakersGlobalVision(
            this.moduleId,
            this.lastSpeakers,
            this.typeOrder,
            (data) => {
                if (data.length > 0) {
                    this.lastSpeakers = data[data.length - 1];
                } else {
                    this.lastSpeakers = null;
                }

                const existingIds = new Set(this.speakers.map(s => s?.uid).filter(Boolean));
                const newData = data
                    .map(element => {
                        element.letter = (element.name[0] || '').toUpperCase();
                        element.principal_title = this.getSpeakerTitle(element.title);
                        return element;
                    })
                    .filter(element => element?.uid && !existingIds.has(element.uid));

                this.speakers = [...this.speakers, ...newData];
                this.speakers = this.getSortedCopy(this.speakers);
                this.loadingMoreSpeakers = false;

                if (event?.target) {
                    event.target.complete();
                    if (!this.lastSpeakers || data.length === 0) {
                        event.target.disabled = true;
                    }
                }
            }
        );
    }

    getSpeakersGroupDividByGroup(group) {
        const groupId = group.uid;
        this.loader = true;
        this.viewGroup = false;

        this.dbSpeakers.getSpeakersGroupGlobalVision(groupId, this.typeOrder, this.moduleId, (data) => {
            this.loader = false;
            this.lastSpeakers = null;
            this.speakers = [];
            this.allSpeakersList = [];

            if (data.length > 0) {
                this.lastSpeakers = data[data.length - 1];
            }

            data.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getSpeakerTitle(element.title);
            });

            this.speakers = data;
            this.allSpeakersList = data;
        });
    }

    getSpeakersGroupVision() {
        this.dbSpeakers.getSpeakersGroupsVision(this.userGroups, this.typeOrder, this.moduleId, (data) => {
            this.loader = false;
            this.lastSpeakers = null;
            this.speakers = [];
            this.allSpeakersList = [];

            if (data.length > 0) {
                this.lastSpeakers = data[data.length - 1];
            }

            data.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getSpeakerTitle(element.title);
            });

            this.speakers = data;
            this.allSpeakersList = data;
        });
    }

    getFirstSpeakersLimitedAccessByGroups() {
        this.dbSpeakers.getFirstASpeakersLimitedAccessByGroups(this.typeOrder, this.moduleId, (data) => {
            if (data.length > 0) {
                this.lastSpeakers = data[data.length - 1];
            }

            data.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getSpeakerTitle(element.title);
            });

            this.speakers = data;
            this.loader = false;
        });
    }

    getSpeakersAllLimitedAccessByGroups() {
        this.subscriptions.push(this.dbSpeakers.getSpeakersAllLimitedAccessByGroup(this.moduleId).subscribe((data) => {
            this.allSpeakersList = data;
        }));
    }

    moreSpeakersLimitedAccessByGroups(event) {
        if (this.lastSpeakers) {
            this.dbSpeakers.getNextPageSpeakersLimitedAccessByGroup(
                this.moduleId,
                this.lastSpeakers,
                this.typeOrder,
                (data) => {
                    this.lastSpeakers = null;

                    if (data.length > 0) {
                        this.lastSpeakers = data[data.length - 1];
                    }

                    const existingIds = new Set(this.speakers.map(s => s?.uid).filter(Boolean));
                    const newSpeakers = data
                        .map(element => {
                            element.letter = (element.name[0] || '').toUpperCase();
                            element.principal_title = this.getSpeakerTitle(element.title);
                            return element;
                        })
                        .filter(element => element?.uid && !existingIds.has(element.uid));

                    this.speakers = [...this.speakers, ...newSpeakers];

                    if (this.lastSpeakers) {
                        if (event?.target) event.target.complete();
                    } else {
                        if (event?.target) event.target.disabled = true;
                    }
                }
            );
        } else {
            if (event?.target) event.target.disabled = true;
        }
    }

    getSpeakerTitle(title) {
        return title ? title[this.language] : '';
    }

    private buildFilterLists(speakers: any[]) {
        let companies: string[] = [], posts: string[] = [];

        speakers.forEach(element => {
            element.principal_title = this.getSpeakerTitle(element.title);
            if (element.company && element.company !== '') companies.push(element.company);
            if (element.principal_title !== '') posts.push(element.principal_title);
        });

        companies = [...companies].sort((a, b) =>
            a.toLowerCase() > b.toLowerCase() ? 1 : a.toLowerCase() < b.toLowerCase() ? -1 : 0
        );
        posts = [...posts].sort((a, b) =>
            a.toLowerCase() > b.toLowerCase() ? 1 : a.toLowerCase() < b.toLowerCase() ? -1 : 0
        );

        if (speakers.some(x => x.company === null || x.company === '')) {
            companies.push(this.translateService.instant('pages.attendees.not_specified'));
        }
        if (speakers.some(x => x.principal_title === '')) {
            posts.push(this.translateService.instant('pages.attendees.not_specified'));
        }

        this.listCompany = Array.from(new Set([...companies]));
        this.listPost = Array.from(new Set([...posts]));
    }

    async showFilterBloc() {
        if (this.isMobile) {
            const list = [];
            if (this.allowFilterCompany) {
                list.push({ key: 'company', value: this.listCompany });
            }
            if (this.allowFilterPost) {
                list.push({ key: 'post', value: this.listPost });
            }
            const modal = await this.modalCtrl.create({
                component: PathComponent.filter_by,
                componentProps: {
                    mode: 'mobile',
                    comp: 'speakers',
                    choices: list,
                    selectedValues: [
                        { key: 'company', value: this.checkedCompanies },
                        { key: 'post', value: this.checkedPosts }
                    ],
                    colors: {
                        'link_color': this.eventColors.link_color,
                        'title_color': this.eventColors.title_color,
                        'btn_text_color': this.eventColors.btn_text_color
                    },
                    companyFilterText: this.companyFilterText,
                    postFilterText: this.postFilterText
                },
                cssClass: 'modal-order-by'
            });

            modal.onDidDismiss().then(ret => {
                this.checkedCompanies = ret.data.selectedValues.filter(x => x.key === 'company')[0].value;
                this.checkedPosts = ret.data.selectedValues.filter(x => x.key === 'post')[0].value;
                this.filterArray(this.checkedPosts, this.checkedCompanies);
            });
            return await modal.present();
        } else {
            this.isFilterOpen = !this.isFilterOpen;
        }
    }

    checkedChanged(ev, type: string) {
        const value = ev.target.value;
        const isChecked = ev.target.checked;
        if (type === 'company') {
            this.checkedCompanies = isChecked
                ? [...this.checkedCompanies, value]
                : this.checkedCompanies.filter(x => x !== value);
            this.checkedCompanies = Array.from(new Set(this.checkedCompanies));
        } else {
            this.checkedPosts = isChecked
                ? [...this.checkedPosts, value]
                : this.checkedPosts.filter(x => x !== value);
            this.checkedPosts = Array.from(new Set(this.checkedPosts));
        }
        this.currentPage = 1;
        this.filterArray(this.checkedPosts, this.checkedCompanies);
    }

    filterArray(posts: Array<any>, companies: Array<any>) {
        const not_specified = this.translateService.instant('pages.attendees.not_specified');
        this.currentPage = 1;

        if (posts.length === 0 && companies.length > 0) {
            this.searchedSpeakers = this.getSortedCopy([...this.allSpeakersList].filter(item => {
                item.principal_title = this.getSpeakerTitle(item.title);
                return ((item.company === null || item.company === '') && companies.includes(not_specified) || companies.includes(item.company)) && item;
            }));
            this.searchedSpeakersFixedArray = [...this.searchedSpeakers];
            return;
        }
        if (posts.length > 0 && companies.length === 0) {
            this.searchedSpeakers = this.getSortedCopy([...this.allSpeakersList].filter(item => {
                item.principal_title = this.getSpeakerTitle(item.title);
                return ((item.principal_title === null || item.principal_title === '') && posts.includes(not_specified) || posts.includes(item.principal_title)) && item;
            }));
            this.searchedSpeakersFixedArray = [...this.searchedSpeakers];
            return;
        }
        if (posts.length > 0 && companies.length > 0) {
            this.searchedSpeakers = this.getSortedCopy([...this.allSpeakersList].filter(item => {
                item.principal_title = this.getSpeakerTitle(item.title);
                return (companies.includes(not_specified) && posts.includes(not_specified) && (item.company === null || item.company === '') && (item.principal_title === null || item.principal_title === '') || posts.includes(item.principal_title) && companies.includes(item.company)) && item;
            }));
            if (this.searchedSpeakers.length === 0) this.showNoResultImage = true;
            this.searchedSpeakersFixedArray = [...this.searchedSpeakers];
            return;
        }
        if (posts.length === 0 && companies.length === 0) {
            this.searchedSpeakers = [];
            this.searchedSpeakersFixedArray = [];
            this.showNoResultImage = false;
        }
    }

    removeItem(arrType, item?: any) {
        switch (arrType) {
            case 'all':
                this.checkedPosts = [];
                this.checkedCompanies = [];
                break;
            case 'post':
                if (item !== undefined && item !== null) {
                    this.checkedPosts = this.checkedPosts.filter(p => p !== item);
                } else {
                    this.checkedPosts = [];
                }
                break;
            case 'company':
                if (item !== undefined && item !== null) {
                    this.checkedCompanies = this.checkedCompanies.filter(p => p !== item);
                } else {
                    this.checkedCompanies = [];
                }
                break;
        }
        this.currentPage = 1;
        this.filterArray(this.checkedPosts, this.checkedCompanies);
        if (this.searchedSpeakers.length === 0) this.showNoResultImage = false;
    }

    getGalleryImages() {
        const sub = this.dbGallery.getSpeakerGalleryImage(this.eventId).subscribe(galleryImages => {
            this.listGalleryPhoto = galleryImages;
        });
        this.subscriptions.push(sub);
    }

    hasUrl(url: string) {
        for (let i = 0; i < this.listGalleryPhoto?.length; i++) {
            if (this.listGalleryPhoto[i].url === url) {
                return true;
            }
        }
        return false;
    }

    /**
     * @description resolve the speaker photo: gallery photo (validated against the gallery) → photoUrl → ''
     * @param speaker
     */
    getSpeakerPhoto(speaker: any): string {
        if (!speaker) return '';
        const galleryObj = speaker.galleryPhoto || speaker.spekerGalleryFoto || null;
        if (galleryObj && typeof galleryObj === 'object') {
            const url = galleryObj.url || galleryObj.photoUrl || null;
            if (url && this.hasUrl(url)) {
                return url;
            }
        }
        return (speaker.photoUrl && speaker.photoUrl !== '') ? speaker.photoUrl : '';
    }
}
